import type { TFunction } from 'i18next'
import type { TimelineItem } from '@/ds'
import { dayMonth, dayMonthLong, dayMonthTime, formatMoney, formatMoneyShort, locale } from '@/lib/format'
import type { Deposit, DepositKind, DepositTimelineItem } from '@/services/fianza'

/**
 * Copy de la fianza según el tipo (inventario D7, comportamiento provisional del 17 sep 2026). Mientras la API
 * no envíe `kind`, todo el texto es neutro y cierto para preautorización y para cargo ("retenida", "quedará
 * liberada"), sin afirmar que se cobra ni que no se cobra. Las frases y los explicadores de cada tipo
 * (`v2.fianza.tipo.preauth.*` / `v2.fianza.tipo.charge.*`) solo aparecen cuando `kind` llega de backend.
 */
export type DepositKindCopy = 'preauth' | 'charge'

export const knownKind = (kind: DepositKind): DepositKindCopy | null => (kind === 'preauth' || kind === 'charge' ? kind : null)

/** Frase específica del tipo, o null si el backend no ha dicho el tipo. */
export function kindText(kind: DepositKind, key: string, t: TFunction, vars: Record<string, unknown> = {}): string | null {
  const known = knownKind(kind)
  return known ? t(`v2.fianza.tipo.${known}.${key}`, vars) : null
}

const join = (...parts: (string | null)[]) => parts.filter((p): p is string => Boolean(p)).join(' ')

/** Plazo del banco: con cifras solo si el PMS lo da; si no, neutro (fase 4, A3). */
export const bankDelayText = (d: Pick<Deposit, 'bankDelayDays'>, t: TFunction): string => (d.bankDelayDays ? t('v2.fianza.descripcion.bancoDias', d.bankDelayDays) : t('v2.fianza.descripcion.banco'))

/** Qué es la fianza antes de reservarla: la misma frase en Tu fianza y en Reservar la fianza. */
export const pendingDepositDescription = (t: TFunction): string => join(t('v2.fianza.descripcion.garantia'), t('v2.fianza.descripcion.liberacion'))

/**
 * Descripción única de la fianza según su estado (fase 4, B2), reutilizada en Tu fianza, Reservar la fianza, Tu estancia,
 * Salida y Después. Neutra sin tipo (D7); plazos y fechas solo si el PMS los da (A3). Con tipo, su frase delante.
 */
export function depositDescription(d: Deposit, lang: string, t: TFunction): string {
  const amount = formatMoneyShort(d.amount, lang)
  switch (d.status) {
    case 'pending':
      return pendingDepositDescription(t)
    case 'held':
      return join(kindText(d.kind, 'estancia', t, { amount }) ?? t('v2.fianza.descripcion.retenida', { amount }), t('v2.fianza.descripcion.liberacion'))
    case 'review':
      return d.reviewDeadline ? t('v2.fianza.descripcion.revisionHasta', { date: dayMonthLong(d.reviewDeadline, lang) }) : t('v2.fianza.descripcion.revision')
    case 'released':
    case 'refunded':
      return join(t('v2.fianza.descripcion.liberada'), d.availableWindow ? t('v2.fianza.descripcion.disponibleEntre', { from: dayMonth(d.availableWindow.from, lang), to: dayMonth(d.availableWindow.to, lang) }) : bankDelayText(d, t))
    case 'deducted': {
      const ded = d.deduction
      return ded ? t('v2.fianza.resumen.deduccionTexto', { released: formatMoneyShort(ded.released, lang), total: new Intl.NumberFormat(locale(lang), { maximumFractionDigits: 2 }).format(d.amount.amount / 100) }) : ''
    }
  }
}

const expectedText = (it: DepositTimelineItem, lang: string, t: TFunction) => {
  if (!it.expected) return undefined
  if (!it.expected.to) return t('v2.fianza.hito.previsto', { date: dayMonth(it.expected.from, lang) })
  // "Entre el 10 y el 17 oct" en el mismo mes (bundle); si no, con los dos meses.
  const [from, to] = [new Date(it.expected.from), new Date(it.expected.to)]
  const sameMonth = from.getMonth() === to.getMonth() && from.getFullYear() === to.getFullYear()
  return t('v2.fianza.hito.entre', { from: sameMonth ? String(from.getDate()) : dayMonth(it.expected.from, lang), to: dayMonth(it.expected.to, lang) })
}

/** Hitos de la línea de tiempo (Pagos.dc.html, PantallaFianza.dc.html). Sin fecha prevista no se inventa (D7). */
export function timelineItems(d: Deposit, lang: string, t: TFunction): TimelineItem[] {
  return d.timeline.map((it): TimelineItem => {
    const date = it.date ? dayMonthTime(it.date, lang) : undefined
    const base = { id: it.code, state: it.state }
    switch (it.code) {
      case 'held': {
        const amount = formatMoneyShort(d.amount, lang)
        const card = d.cardLast4 ? t('v2.fianza.hito.apartadaTarjeta', { amount, last4: d.cardLast4 }) : t('v2.fianza.hito.apartadaTuTarjeta', { amount })
        const description = it.state === 'done' ? join(card, kindText(d.kind, 'hitoRetenida', t)) : t('v2.fianza.hito.apartadaPendiente')
        return { ...base, title: t('v2.fianza.hito.apartada'), ...(date ? { date } : {}), description }
      }
      case 'stay_ended':
        return { ...base, title: t('v2.fianza.hito.terminada'), date: date ?? (it.expected ? dayMonthTime(it.expected.from, lang) : '') }
      case 'review':
        return { ...base, title: t('v2.fianza.hito.revision'), ...(date ? { date } : it.expected?.to ? { date: t('v2.fianza.hito.hasta', { date: dayMonthTime(d.reviewDeadline ?? it.expected.to, lang) }) } : {}), description: t('v2.fianza.descripcion.revision') }
      case 'released':
        return {
          ...base,
          title: t('v2.fianza.hito.liberada'),
          date: date ?? expectedText(it, lang, t) ?? t('v2.fianza.hito.alTerminar'),
          description: bankDelayText(d, t),
        }
      case 'deducted': {
        const ded = d.deduction
        return {
          ...base,
          title: t('v2.fianza.hito.deduccion'),
          ...(date ? { date } : {}),
          ...(ded ? { description: t('v2.fianza.hito.deduccionTexto', { deducted: formatMoneyShort(ded.deducted, lang), reason: ded.reason, released: formatMoneyShort(ded.released, lang) }) } : {}),
          tone: 'error',
        }
      }
      case 'available':
        return {
          ...base,
          title: t('v2.fianza.hito.disponible'),
          ...((): { date?: string } => {
            const date = expectedText(it, lang, t) ?? (d.bankDelayDays ? t('v2.fianza.hito.diasDespues', d.bankDelayDays) : undefined)
            return date ? { date } : {}
          })(),
          ...(knownKind(d.kind) ? { description: kindText(d.kind, 'disponible', t) ?? '' } : {}),
        }
    }
  })
}

/** Tarjeta resumen: etiqueta, importe, chip (siempre con icono y texto) y una frase. */
export function summaryFor(d: Deposit, lang: string, t: TFunction): { label: string; amount: string; chip: { tone: 'success' | 'warning' | 'error' | 'neutral'; text: string }; text: string } {
  const amount = formatMoney(d.amount, lang)
  const text = depositDescription(d, lang, t)
  switch (d.status) {
    case 'pending':
      return { label: t('v2.fianza.resumen.aReservar'), amount, chip: { tone: 'neutral', text: t('v2.fianza.resumen.pendiente') }, text }
    case 'held':
      return { label: d.holdUntil ? t('v2.fianza.resumen.retenidaHasta', { date: dayMonth(d.holdUntil, lang) }) : t('v2.fianza.resumen.retenidaSinFecha'), amount, chip: { tone: 'warning', text: t('v2.fianza.resumen.retenida') }, text }
    case 'review':
      return { label: t('v2.fianza.resumen.enRevision'), amount, chip: { tone: 'warning', text: d.reviewDeadline ? t('v2.fianza.resumen.enRevisionMax', { date: dayMonth(d.reviewDeadline, lang) }) : t('v2.fianza.resumen.enRevision') }, text }
    case 'released':
    case 'refunded':
      return { label: t('v2.fianza.resumen.liberada'), amount: formatMoney(d.releasedAmount ?? d.amount, lang), chip: { tone: 'success', text: d.releasedAt ? t('v2.fianza.resumen.liberadaEl', { date: dayMonth(d.releasedAt, lang) }) : t('v2.fianza.resumen.liberada') }, text }
    case 'deducted': {
      const ded = d.deduction
      return { label: t('v2.fianza.resumen.seLiberan'), amount: formatMoney(ded?.released ?? d.releasedAmount ?? d.amount, lang), chip: { tone: 'error', text: t('v2.fianza.resumen.deduccionDe', { amount: formatMoneyShort(ded?.deducted ?? d.amount, lang) }) }, text }
    }
  }
}


/**
 * Explicador del tipo. El PMS aún no informa siempre el tipo de fianza; por decisión de producto se
 * solo se muestra cuando el backend ha confirmado el tipo. Si no llega, el copy de la fianza sigue siendo neutro.
 */
export function explainerFor(d: Pick<Deposit, 'kind' | 'bankDelayDays'>, t: TFunction): { title: string; text: string } | null {
  const kind = knownKind(d.kind)
  if (!kind) return null
  return { title: t(`v2.fianza.tipo.${kind}.explicadorTitulo`), text: join(t(`v2.fianza.tipo.${kind}.explicadorTexto`), bankDelayText(d, t)) }
}
